// Copyright (c) 2025 by Audere

import { Question } from "../features/label/labelTypes";

export interface QuestionResponse {
  response?: string | string[];
  requiredComment?: string | string[];
}

// Return true if value is non-empty string after trimming
function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

// Return true if value is non-empty array
function isNonEmptyArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.length > 0;
}

// Return true if value is a non-empty string or non-empty array
export function isValidValue(value: unknown): boolean {
  if (value === undefined) return false;
  return Array.isArray(value)
    ? isNonEmptyArray(value)
    : isNonEmptyString(value);
}

/**
 * Returns true if:
 * 1. trigger contains any element of userValue when both are arrays,
 * 2. trigger contains userValue when trigger is an array,
 * 3. userValue contains trigger when userValue is an array,
 * 4. or trigger === userValue when both are strings.
 */
export function valueMatches(
  trigger: string | string[],
  userValue: string | string[]
): boolean {
  if (Array.isArray(trigger)) {
    return Array.isArray(userValue)
      ? userValue.some((value) => trigger.includes(value))
      : trigger.includes(userValue);
  } else {
    return Array.isArray(userValue)
      ? userValue.includes(trigger)
      : userValue === trigger;
  }
}

/**
 * Validates the question response according to:
 * 1. For required questions, response must be valid (non-empty).
 * 2. If a comment is required (addComment true and commentRequired defined)
 *    and the response matches the trigger, then requiredComment must also be valid.
 * 3. Non-required questions are always valid.
 */
export function validateQuestion(
  question: Question,
  answer: QuestionResponse
): boolean {
  if (!question.isRequired) return true; // Non-required questions are always valid

  if (!answer.response) return false; // Required question with no response

  if (!isValidValue(answer.response)) return false; // Response invalid

  // Ensure requiredComment is valid if response triggered an additional comment
  if (
    question.addComment &&
    question.commentRequired !== undefined &&
    valueMatches(question.commentRequired, answer.response)
  ) {
    return isValidValue(answer.requiredComment);
  }

  return true;
}
